// One flattener for rejected writes. Every admin surface that toasts a failed
// mutation used to carry its own copy, and the copies had drifted: the anchor
// ones read `errors`, the others read `code`, so the same rejected
// `anchors.setForChapter` showed the violation list on one surface and a bare
// "anchors_invalid" on another. One helper, one order of preference.

import { ConvexError } from "convex/values";

/** `ConvexError.data` is typed `any` by Convex — this is the shape we look for. */
interface ConvexErrorData {
  /** Violation list from a rejected `anchors.setForChapter`. */
  errors?: unknown;
  /** Human-written explanation thrown by the mutation. */
  message?: unknown;
  /** Machine tag (`"anchors_invalid"`), the last resort before the JS message. */
  code?: unknown;
}

/** `ConvexError` may carry any Convex value — a bare string or `null` included. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Flatten a rejected write (`ConvexError` included) into toast copy.
 *
 * `errors` comes first because the violation list is the only part an admin can
 * act on — the generic `message` above it ("would violate invariants") says
 * nothing — and `code` comes last because it is a machine tag, readable only
 * when nothing better was thrown.
 *
 * @param fallback shown when the throw carried no message at all — not even a
 * JS `Error`. Phrase it for the surface doing the catching.
 */
export function errorMessage(
  error: unknown,
  fallback = "Something went wrong.",
): string {
  if (error instanceof ConvexError && isRecord(error.data)) {
    const data = error.data as ConvexErrorData;
    if (Array.isArray(data.errors)) return data.errors.join("; ");
    if (typeof data.message === "string") return data.message;
    if (typeof data.code === "string") return data.code;
  }
  return error instanceof Error ? error.message : fallback;
}
